<script setup>
import getUuid from 'widget/helpers/uuid';
import { ref, onMounted, onUnmounted, defineEmits, defineExpose } from 'vue';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js';
import { format, intervalToDuration } from 'date-fns';
import { convertAudio } from './utils/mp3ConversionUtils';

const props = defineProps({
  audioRecordFormat: {
    type: String,
    required: true,
  },
});

const emit = defineEmits([
  'recorderProgressChanged',
  'finishRecord',
  'pause',
  'play',
]);

const waveformContainer = ref(null);
const wavesurfer = ref(null);
const record = ref(null);
const isRecording = ref(false);
const isPlaying = ref(false);
const hasRecording = ref(false);

const formatTimeProgress = time => {
  const duration = intervalToDuration({ start: 0, end: time });
  return format(
    new Date(0, 0, 0, 0, duration.minutes, duration.seconds),
    'mm:ss'
  );
};

const initWaveSurfer = () => {
  // v6.3.95 HOTFIX: Determinar mimeType correto para gravação
  // WhatsApp requer 'audio/ogg; codecs=opus', não aceita 'audio/webm'
  // Browsers Chrome/Edge suportam gravação direta em OGG/Opus
  let recordMimeType = 'audio/webm'; // default
  
  if (props.audioRecordFormat === 'audio/ogg') {
    // Tentar OGG primeiro (WhatsApp Voice Messages)
    if (MediaRecorder.isTypeSupported('audio/ogg; codecs=opus')) {
      recordMimeType = 'audio/ogg; codecs=opus';
    } else if (MediaRecorder.isTypeSupported('audio/ogg')) {
      recordMimeType = 'audio/ogg';
    }
    // Se nenhum OGG suportado, vai usar webm e converter no backend
  }

  wavesurfer.value = WaveSurfer.create({
    container: waveformContainer.value,
    waveColor: '#1F93FF',
    progressColor: '#6E6F73',
    height: 100,
    barWidth: 2,
    barGap: 1,
    barRadius: 2,
    plugins: [
      RecordPlugin.create({
        scrollingWaveform: true,
        renderRecordedAudio: false,
        mimeType: recordMimeType, // v6.3.95: Gravar no formato correto
      }),
    ],
  });

  wavesurfer.value.on('pause', () => emit('pause'));
  wavesurfer.value.on('play', () => emit('play'));

  record.value = wavesurfer.value.plugins[0];

  wavesurfer.value.on('finish', () => {
    isPlaying.value = false;
  });

  record.value.on('record-end', async blob => {
    const audioUrl = URL.createObjectURL(blob);
    
    let audioBlob;
    let fileName;
    
    // v6.3.95: Verificar o tipo real do blob gravado
    const blobMimeType = blob.type;
    console.log('[AudioRecorder] Blob mimeType:', blobMimeType, 'Target format:', props.audioRecordFormat);
    
    // OGG: usar blob nativo se já é OGG, senão será convertido no backend
    if (props.audioRecordFormat === 'audio/ogg') {
      // Se o browser gravou em OGG nativo, usar direto
      if (blobMimeType.includes('ogg')) {
        audioBlob = blob;
        fileName = `${getUuid()}.ogg`;
      } else {
        // Browser não suporta OGG nativo, enviar webm para conversão backend
        // Mas precisamos sinalizar isso de alguma forma
        // Por ora, enviamos como está e o backend converte via FFmpeg
        audioBlob = blob;
        fileName = `${getUuid()}.webm`;
        console.warn('[AudioRecorder] Browser não suporta OGG nativo, usando WebM para conversão backend');
      }
    } else {
      // MP3/WAV: converter como antes
      audioBlob = await convertAudio(blob, props.audioRecordFormat);
      fileName = props.audioRecordFormat === 'audio/mp3' 
        ? `${getUuid()}.mp3` 
        : `${getUuid()}.wav`;
    }
    
    // v6.3.95: Usar o mimeType real do blob, não o desejado
    const fileType = audioBlob.type || props.audioRecordFormat;
    
    const file = new File([audioBlob], fileName, {
      type: fileType,
    });
    wavesurfer.value.load(audioUrl);
    emit('finishRecord', {
      name: file.name,
      type: file.type,
      size: file.size,
      file,
    });
    hasRecording.value = true;
    isRecording.value = false;
  });

  record.value.on('record-progress', time => {
    emit('recorderProgressChanged', formatTimeProgress(time));
  });
};

const stopRecording = () => {
  if (isRecording.value) {
    record.value.stopRecording();
    isRecording.value = false;
  }
};

const startRecording = () => {
  record.value.startRecording();
  isRecording.value = true;
};

const playPause = () => {
  if (hasRecording.value) {
    wavesurfer.value.playPause();
    isPlaying.value = !isPlaying.value;
  }
};

onMounted(() => {
  initWaveSurfer();
  startRecording();
});

onUnmounted(() => {
  if (wavesurfer.value) {
    wavesurfer.value.destroy();
  }
});

defineExpose({ playPause, stopRecording, record });
</script>

<template>
  <div ref="waveformContainer" class="w-full p-1" />
</template>
